import { bearingDegrees, haversineKm } from '../utils/geo';

/**
 * Live map demo: a fake fleet driving real Sarajevo streets, computed in the browser.
 * Positions and ride statuses are pure functions of the elapsed demo time; nothing is
 * sent to the API. Street geometry comes from liveMapDemoGeography.json
 * (regenerate with `node scripts/generateLiveMapDemoRoutes.js`).
 */

/** Each demo car reports a new location this often, like a polled driver. */
export const LIVE_MAP_DEMO_UPDATE_INTERVAL_MS = 6000;

/** The company whose dispatcher board always shows the demo instead of live data. */
export const LIVE_MAP_DEMO_COMPANY_CODE = 'mojtaxi';

export const isLiveMapDemoCompany = (company) =>
    String(company?.company_code ?? '').trim().toLowerCase() === LIVE_MAP_DEMO_COMPANY_CODE;

// Far above real ids so a demo row can never be mistaken for a real driver, car or stand.
const DEMO_ID_OFFSET = 900000;

const kmhToMetersPerMs = (kmh) => kmh / 3600;

// The same drivers and cars appear in every run; what they are doing is drawn anew each time.
const MOVING_DRIVERS = [
    { name: 'Amer Hodžić', carNumber: '12', carName: 'Škoda Octavia', registration: 'E41-K-215' },
    { name: 'Nemanja Petrović', carNumber: '27', carName: 'Volkswagen Passat', registration: 'K12-M-834' },
    { name: 'Edin Mujić', carNumber: '8', carName: 'Toyota Corolla', registration: 'A27-E-560' },
    { name: 'Dragan Kovačević', carNumber: '31', carName: 'Dacia Logan', registration: 'M33-J-178' },
    { name: 'Haris Begić', carNumber: '15', carName: 'Škoda Superb', registration: 'J05-T-942' },
    { name: 'Slaviša Jovanović', carNumber: '22', carName: 'Hyundai i40', registration: 'T19-A-307' },
    { name: 'Adnan Karić', carNumber: '4', carName: 'Volkswagen Golf', registration: 'O62-K-451' },
    { name: 'Goran Savić', carNumber: '19', carName: 'Opel Insignia', registration: 'E77-O-623' },
    { name: 'Mirza Delić', carNumber: '33', carName: 'Kia Ceed', registration: 'A14-M-789' },
    { name: 'Senad Hadžić', carNumber: '6', carName: 'Toyota Prius', registration: 'K58-J-036' },
];

const STAND_DRIVERS = [
    { name: 'Kenan Alić', carNumber: '2', carName: 'Škoda Octavia', registration: 'M20-E-114', standId: 'bascarsija' },
    { name: 'Zoran Ilić', carNumber: '17', carName: 'Volkswagen Passat', registration: 'J71-A-905', standId: 'zeljeznicka' },
    { name: 'Emir Softić', carNumber: '24', carName: 'Toyota Auris', registration: 'T03-K-372', standId: 'lukavica' },
];

const PAUSED_DRIVERS = [
    { name: 'Milan Todorović', carNumber: '11', carName: 'Dacia Duster', registration: 'O48-T-259' },
    { name: 'Jasmin Kurtović', carNumber: '29', carName: 'Peugeot 508', registration: 'E90-J-681' },
];

// Speeds stay around 40 km/h; slight differences keep the cars from moving in lockstep.
const SPEED_KMH = { min: 36, max: 44 };

// Busy drivers carry their own street fares (busy feature flag only): no rides, short stops.
const BUSY_DRIVER_COUNT = 2;
const BUSY_STOP_SECONDS = { min: 30, max: 90 };

// When the demo opens, this share of the free cars has a passenger on board; the rest are
// on their way to a customer.
const STARTED_RIDE_SHARE = 0.6;

// Each car shuttles between the two ends of its route. After a drop-off it stands for a
// moment; the next customer is either right there (a trip back) or waiting at the other
// end, in which case the car drives there empty, waits for them and brings them back.
const DROP_OFF_SECONDS = { min: 20, max: 50 };
const PICK_UP_SECONDS = { min: 25, max: 70 };
const NEXT_RIDE_FROM_HERE_SHARE = 0.6;

// Some customers book while the car is still on its previous ride (the "+1" in the panel).
const QUEUED_RIDE_SHARE = 0.25;
const QUEUED_RIDE_MINUTES_AHEAD = { min: 1, max: 3 };

// Longer than any presentation; afterwards the cars stay where they are.
const SCHEDULE_HOURS = 12;

const RIDE_NOTES = [
    'Dva kofera, potreban gepek',
    'Pozvati po dolasku',
    'Ulaz iz dvorišta',
    'Plaćanje karticom',
    'Dijete, potrebna sjedalica',
    'Čeka ispred ulaza',
    'Pozvoniti na interfon',
    'Putuje sa kućnim ljubimcem',
];
const NOTE_CHANCE = 0.35;

// Earlier today, so the statistic tiles look like a working day.
const FINISHED_RIDES_TODAY = { min: 25, max: 45 };
const REFUSED_RIDES_TODAY = { min: 1, max: 5 };

const randomBetween = (random, min, max) => min + random() * (max - min);
const randomInt = (random, min, max) => Math.floor(randomBetween(random, min, max + 1));
const pickRandom = (random, items) => items[Math.floor(random() * items.length)];
const randomSeconds = (random, { min, max }) => randomBetween(random, min, max) * 1000;
const randomMinutes = (random, { min, max }) => randomBetween(random, min, max) * 60000;

/** Fisher–Yates shuffle of a copy. */
const shuffled = (random, items) => {
    const copy = [...items];
    for (let i = copy.length - 1; i > 0; i -= 1) {
        const j = Math.floor(random() * (i + 1));
        [copy[i], copy[j]] = [copy[j], copy[i]];
    }
    return copy;
};

const otherEnd = (end) => (end === 'start' ? 'end' : 'start');

/** Cumulative distances along a [lat, lng] polyline, so positions can be looked up by meters. */
export const preparePath = (points) => {
    const cumulative = [0];
    for (let i = 1; i < points.length; i += 1) {
        const [lat1, lng1] = points[i - 1];
        const [lat2, lng2] = points[i];
        cumulative.push(cumulative[i - 1] + haversineKm(lat1, lng1, lat2, lng2) * 1000);
    }
    return { points, cumulative, length: cumulative[cumulative.length - 1] ?? 0 };
};

/** Position and direction of travel `distance` meters along a prepared path. */
export const pointAtDistance = ({ points, cumulative, length }, distance) => {
    const last = points.length - 1;
    if (last <= 0 || length <= 0) {
        const [lat, lng] = points[0];
        return { lat, lng, heading: null };
    }

    const d = Math.min(Math.max(distance, 0), length);
    let low = 0;
    let high = last;
    while (high - low > 1) {
        const mid = (low + high) >> 1;
        if (cumulative[mid] <= d) low = mid;
        else high = mid;
    }

    const [lat1, lng1] = points[low];
    const [lat2, lng2] = points[high];
    const span = cumulative[high] - cumulative[low];
    const t = span > 0 ? (d - cumulative[low]) / span : 0;
    return {
        lat: lat1 + (lat2 - lat1) * t,
        lng: lng1 + (lng2 - lng1) * t,
        heading: span > 0 ? bearingDegrees(lat1, lng1, lat2, lng2) : null,
    };
};

/** The stretch of path between two distances, including every corner in between. */
const slicePath = (path, from, to) => {
    const start = pointAtDistance(path, from);
    const end = pointAtDistance(path, to);
    const slice = [[start.lat, start.lng]];
    path.cumulative.forEach((distance, i) => {
        if (distance > from && distance < to) slice.push(path.points[i]);
    });
    slice.push([end.lat, end.lng]);
    return slice;
};

const lastPoint = (path) => path.points[path.points.length - 1];

/**
 * Builds a moving car's drive/stop timeline for the whole demo and, for a free car, the
 * rides it carries. Times are milliseconds since the demo opened; a ride ordered before
 * that has a negative time. `opening` is 'started' (passenger on board, driving them to
 * the route's end) or 'accepted' (driving to a customer at the route's end).
 */
const scheduleCar = (car, { random, opening, queuedAtStart, addRide }) => {
    const phases = [];
    let t = 0;
    let at = 'start';
    const drive = () => {
        const to = otherEnd(at);
        const durationMs = car.paths[to].length / car.speed;
        phases.push({ kind: 'drive', to, fromMs: t, toMs: t + durationMs });
        t += durationMs;
        at = to;
    };
    const stop = (durationMs) => {
        phases.push({ kind: 'stop', at, fromMs: t, toMs: t + durationMs });
        t += durationMs;
    };
    const horizonMs = SCHEDULE_HOURS * 3600000;

    if (!opening) {
        while (t < horizonMs) {
            drive();
            stop(randomSeconds(random, BUSY_STOP_SECONDS));
        }
        car.phases = phases;
        return;
    }

    let ride;
    if (opening === 'started') {
        ride = addRide(car, {
            pickup: 'start',
            destination: 'end',
            orderedAtMs: -randomMinutes(random, { min: 4, max: 25 }),
            startedAtMs: -randomSeconds(random, { min: 0, max: 30 }),
        });
        drive();
    } else {
        ride = addRide(car, {
            pickup: 'end',
            destination: 'start',
            orderedAtMs: -randomMinutes(random, { min: 0.5, max: 5 }),
        });
        drive();
        stop(randomSeconds(random, PICK_UP_SECONDS));
        ride.startedAtMs = t;
        drive();
    }
    ride.finishedAtMs = t;

    let bookedAtOpening = queuedAtStart;
    while (t < horizonMs) {
        const previous = ride;
        const queued = bookedAtOpening || random() < QUEUED_RIDE_SHARE;

        if (queued || random() < NEXT_RIDE_FROM_HERE_SHARE) {
            // The customer waits where the last one got out: booked during the drop-off,
            // or already while the previous ride was under way.
            let orderedAtMs = t + randomSeconds(random, { min: 5, max: 15 });
            if (bookedAtOpening) {
                orderedAtMs = -randomSeconds(random, { min: 0, max: 60 });
            } else if (queued) {
                orderedAtMs = Math.max(
                    previous.startedAtMs + 10000,
                    previous.finishedAtMs - randomMinutes(random, QUEUED_RIDE_MINUTES_AHEAD)
                );
            }
            stop(randomSeconds(random, DROP_OFF_SECONDS));
            ride = addRide(car, { pickup: at, destination: otherEnd(at), orderedAtMs, startedAtMs: t });
            drive();
        } else {
            const orderedAtMs = t + randomSeconds(random, { min: 5, max: 15 });
            stop(randomSeconds(random, DROP_OFF_SECONDS));
            ride = addRide(car, { pickup: otherEnd(at), destination: at, orderedAtMs });
            drive();
            stop(randomSeconds(random, PICK_UP_SECONDS));
            ride.startedAtMs = t;
            drive();
        }
        ride.finishedAtMs = t;
        bookedAtOpening = false;
    }
    car.phases = phases;
};

const minutesAgoIso = (startedAtMs, minutes) =>
    new Date(startedAtMs - minutes * 60000).toISOString();

/**
 * Builds the fake fleet once per demo run. The drivers, cars and stands never change, but
 * each run draws 10 of the pooled routes, the rides on them, speeds, busy drivers, pause
 * spots and the day's history, so no two demos look alike. Moving cars keep shuttling
 * between their route's ends with new customers for hours. `random` returns [0, 1) like
 * Math.random; tests pass a seeded one.
 */
export const buildLiveMapDemoFleet = (geography, { withBusy, startedAtMs, random = Math.random }) => {
    const stands = geography.stands.map((stand, index) => ({
        stand_id: DEMO_ID_OFFSET + index + 1,
        key: stand.id,
        name: stand.name,
        description: stand.description,
        latitude: stand.latitude,
        longitude: stand.longitude,
        carLatitude: stand.carLatitude,
        carLongitude: stand.carLongitude,
        priority: index + 1,
        capacity: 5,
    }));

    const cars = [];
    const pushCar = (definition, extra) => {
        const index = cars.length + 1;
        const car = {
            ...definition,
            ...extra,
            driverId: DEMO_ID_OFFSET + index,
            carId: DEMO_ID_OFFSET + index,
            // A driver's rating belongs to the driver, so it stays the same between runs.
            rating: (45 + ((index * 7) % 6)) / 10,
            shiftMinutes: randomInt(random, 25, 240),
        };
        cars.push(car);
        return car;
    };

    const routes = shuffled(random, geography.routes).slice(0, MOVING_DRIVERS.length);
    const busyDrivers = new Set(
        withBusy ? shuffled(random, MOVING_DRIVERS).slice(0, BUSY_DRIVER_COUNT) : []
    );
    const movingCars = MOVING_DRIVERS.map((driver, index) => {
        const route = routes[index];
        const forward = preparePath(route.points);
        const back = preparePath(route.returnPoints);
        const place = (address, [latitude, longitude]) => ({ address, latitude, longitude });
        return pushCar(driver, {
            // paths[x] is the way to end x.
            paths: { end: forward, start: back },
            places: {
                start: place(route.startAddress, route.points[0]),
                end: place(route.endAddress, lastPoint(forward)),
            },
            speed: kmhToMetersPerMs(randomBetween(random, SPEED_KMH.min, SPEED_KMH.max)),
            status: busyDrivers.has(driver) ? 'busy' : 'active',
        });
    });

    STAND_DRIVERS.forEach((driver) => {
        const stand = stands.find((item) => item.key === driver.standId);
        if (!stand) throw new Error(`Live map demo geography has no stand '${driver.standId}'`);
        pushCar(driver, {
            position: [stand.carLatitude, stand.carLongitude],
            standId: stand.stand_id,
            status: 'active',
        });
    });

    const pauseSpots = shuffled(random, geography.parkedSpots);
    PAUSED_DRIVERS.forEach((driver, index) => {
        const spot = pauseSpots[index];
        pushCar(driver, { position: [spot.latitude, spot.longitude], status: 'pause' });
    });

    let driveId = DEMO_ID_OFFSET;
    const rides = [];
    const addRide = (car, ride) => {
        driveId += 1;
        const entry = {
            id: driveId,
            car,
            note: random() < NOTE_CHANCE ? pickRandom(random, RIDE_NOTES) : null,
            ...ride,
        };
        rides.push(entry);
        return entry;
    };

    const freeCars = shuffled(random, movingCars.filter((car) => car.status === 'active'));
    const startedCount = Math.round(freeCars.length * STARTED_RIDE_SHARE);
    const queuedCar = startedCount > 0 ? pickRandom(random, freeCars.slice(0, startedCount)) : null;
    freeCars.forEach((car, index) =>
        scheduleCar(car, {
            random,
            opening: index < startedCount ? 'started' : 'accepted',
            queuedAtStart: car === queuedCar,
            addRide,
        })
    );
    movingCars
        .filter((car) => car.status === 'busy')
        .forEach((car) => scheduleCar(car, { random, opening: null, addRide }));

    const historyDrives = Array.from(
        { length: randomInt(random, FINISHED_RIDES_TODAY.min, FINISHED_RIDES_TODAY.max) },
        () => {
            driveId += 1;
            return {
                drive_id: driveId,
                fk_driver_id: pickRandom(random, cars).driverId,
                status: 'finished',
                start_datetime: minutesAgoIso(startedAtMs, randomBetween(random, 30, 360)),
                is_live_map_demo: true,
            };
        }
    );

    const refusedDrives = Array.from(
        { length: randomInt(random, REFUSED_RIDES_TODAY.min, REFUSED_RIDES_TODAY.max) },
        (_, i) => ({
            id: `live-map-demo-refused-${i + 1}`,
            fk_driver_id: pickRandom(random, cars).driverId,
            fk_drive_id: DEMO_ID_OFFSET + 50000 + i,
        })
    );

    const carAssignments = cars.map((car) => ({
        driver_id: car.driverId,
        driver_name: car.name,
        car_name: car.carName,
        car_number: car.carNumber,
        registration_number: car.registration,
    }));

    let recordId = 0;
    const statusRecord = (car, status, minutes) => {
        recordId += 1;
        return {
            id: `live-map-demo-${recordId}`,
            fk_driver_id: car.driverId,
            fk_car_id: car.carId,
            status,
            datetime: minutesAgoIso(startedAtMs, minutes),
        };
    };

    const statusList = cars.flatMap((car) => {
        const records = [statusRecord(car, 'active', car.shiftMinutes)];
        if (car.status !== 'active') {
            records.push(statusRecord(car, car.status, randomInt(random, 2, 18)));
        }
        return records;
    });

    return {
        startedAtMs,
        cars,
        stands: stands.map(({ key, carLatitude, carLongitude, ...stand }) => stand),
        carAssignments,
        statusList,
        rides,
        historyDrives,
        refusedDrives,
    };
};

export const isLiveMapDemoDrive = (drive) => drive?.is_live_map_demo === true;

const rideStatusAt = (ride, elapsedMs) => {
    if (ride.startedAtMs == null || elapsedMs < ride.startedAtMs) return 'accepted';
    if (elapsedMs < ride.finishedAtMs) return 'started';
    return 'finished';
};

/**
 * Drives shaped like GET /drives/details rows as of `elapsedMs` into the demo: every ride
 * booked by then with its current status, plus the finished rides from earlier today.
 */
export const getLiveMapDemoDrives = (fleet, elapsedMs) => {
    const rows = fleet.rides
        .filter((ride) => ride.orderedAtMs <= elapsedMs)
        .map((ride) => {
            const { car } = ride;
            const pickup = car.places[ride.pickup];
            const destination = car.places[ride.destination];
            return {
                drive_id: ride.id,
                fk_driver_id: car.driverId,
                driver_id: car.driverId,
                fk_car_id: car.carId,
                name: car.name,
                status: rideStatusAt(ride, elapsedMs),
                start_datetime: new Date(fleet.startedAtMs + ride.orderedAtMs).toISOString(),
                pickup_address: pickup.address,
                start_latitude: pickup.latitude,
                start_longitude: pickup.longitude,
                destination_address: destination.address,
                end_latitude: destination.latitude,
                end_longitude: destination.longitude,
                note: ride.note,
                is_live_map_demo: true,
            };
        });
    return [...rows, ...fleet.historyDrives];
};

const phaseAt = (car, elapsedMs) =>
    car.phases.find((phase) => elapsedMs < phase.toMs) ?? car.phases[car.phases.length - 1];

const stopPoint = (car, end) => lastPoint(car.paths[end]);

/** Where a car is `elapsedMs` into the demo, as [lat, lng]. */
const positionAt = (car, elapsedMs) => {
    if (!car.phases) return car.position;
    const phase = phaseAt(car, elapsedMs);
    if (phase.kind === 'stop') return stopPoint(car, phase.at);
    const { lat, lng } = pointAtDistance(
        car.paths[phase.to],
        (elapsedMs - phase.fromMs) * car.speed
    );
    return [lat, lng];
};

/** The streets a car covered between two moments; a single point if it stood still. */
const trailBetween = (car, fromMs, toMs) => {
    if (!car.phases) return [car.position];

    const points = [positionAt(car, fromMs)];
    car.phases.forEach((phase) => {
        if (phase.toMs <= fromMs || phase.fromMs >= toMs) return;
        if (phase.kind === 'stop') {
            points.push(stopPoint(car, phase.at));
            return;
        }
        const from = (Math.max(fromMs, phase.fromMs) - phase.fromMs) * car.speed;
        const to = (Math.min(toMs, phase.toMs) - phase.fromMs) * car.speed;
        points.push(...slicePath(car.paths[phase.to], from, to));
    });
    points.push(positionAt(car, toMs));

    return points.filter(
        (point, i) => i === 0 || point[0] !== points[i - 1][0] || point[1] !== points[i - 1][1]
    );
};

/**
 * Driver rows (shaped like GET /drivers) for the given update tick. `demo_path` is the
 * street stretch covered since the previous tick, which the map animates over one interval.
 */
export const getLiveMapDemoDrivers = (fleet, tick) => {
    const toMs = tick * LIVE_MAP_DEMO_UPDATE_INTERVAL_MS;
    const fromMs = Math.max(0, toMs - LIVE_MAP_DEMO_UPDATE_INTERVAL_MS);

    return fleet.cars.map((car) => {
        const demoPath = trailBetween(car, fromMs, toMs);
        const [latitude, longitude] = demoPath[demoPath.length - 1];
        return {
            driver_id: car.driverId,
            name: car.name,
            activity_status: car.status,
            latitude,
            longitude,
            average_rating: car.rating,
            stand_id: car.standId ?? null,
            is_live_map_demo: true,
            demo_path: demoPath,
        };
    });
};
